import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Carrier, CreateCarrierDto, UpdateCarrierDto } from '../../models/forwarding/carrier.model';

@Injectable({
  providedIn: 'root'
})
export class CarrierService {
  private apiUrl = `${environment.apiUrl}/forwarding/carriers`;
  private carriersSubject = new BehaviorSubject<Carrier[]>([]);
  public carriers$ = this.carriersSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadCarriers();
  }

  private loadCarriers(): void {
    console.log('[CarrierService] Loading carriers from:', this.apiUrl);
    this.http.get<Carrier[]>(this.apiUrl).subscribe({
      next: (carriers) => {
        console.log('[CarrierService] Successfully loaded carriers from API:', carriers.length, carriers);
        this.carriersSubject.next(carriers);
      },
      error: (error) => {
        console.error('[CarrierService] Error loading carriers from API, falling back to mock data:', error);
        const mockCarriers = this.getMockCarriers();
        console.log('[CarrierService] Using mock data:', mockCarriers.length, 'items');
        this.carriersSubject.next(mockCarriers);
      }
    });
  }

  getCarriers(): Observable<Carrier[]> {
    return this.carriers$;
  }

  getById(id: number): Observable<Carrier> {
    return this.http.get<Carrier>(`${this.apiUrl}/${id}`);
  }

  create(dto: CreateCarrierDto): Observable<Carrier> {
    return this.http.post<Carrier>(this.apiUrl, dto).pipe(
      tap(() => {
        console.log('[CarrierService] Carrier created, reloading carriers');
        this.loadCarriers();
      })
    );
  }

  update(id: number, dto: UpdateCarrierDto): Observable<Carrier> {
    return this.http.put<Carrier>(`${this.apiUrl}/${id}`, dto).pipe(
      tap(() => {
        console.log('[CarrierService] Carrier updated, reloading carriers');
        this.loadCarriers();
      })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      tap(() => {
        console.log('[CarrierService] Carrier deleted, reloading carriers');
        this.loadCarriers();
      })
    );
  }

  private getMockCarriers(): Carrier[] {
    return [
      {
        id: 1,
        code: 'COSCO',
        name: 'COSCO Shipping Lines',
        modes: ['FCL', 'LCL'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 2,
        code: 'EMIRATES',
        name: 'Emirates SkyCargo',
        modes: ['Air'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 3,
        code: 'EVERGREEN',
        name: 'Evergreen Marine',
        modes: ['FCL', 'LCL'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 4,
        code: 'LUFTHANSA',
        name: 'Lufthansa Cargo',
        modes: ['Air'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 5,
        code: 'MAERSK',
        name: 'Maersk Line',
        modes: ['FCL', 'LCL'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 6,
        code: 'MSC',
        name: 'Mediterranean Shipping Company',
        modes: ['FCL', 'LCL'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      },
      {
        id: 7,
        code: 'OOCL',
        name: 'Orient Overseas Container Line',
        modes: ['FCL', 'LCL'],
        regions: null,
        isActive: true,
        createdAt: new Date()
      }
    ];
  }
}
